import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  WallboardEditForm,
  WallboardEditInitialValues,
  WallboardComponents,
  WallboardAdditionalParams,
  WallboardFormOptions,
  onWallboardFormSaved,
  IWallboardFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedWallboard, ISharedPatchWallboard, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchWallboard, patchWallboard, getWallboardUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, useUsers, useStatsReports, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedWallboard> {}

const WallboardFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();
  const users = useUsers();
  const stats_reports = useStatsReports();

  useEffect(() => {
    fetchWallboard(id).then(wallboard => setValue(wallboard));
  }, [id, setValue, ]);

  const param_props: IWallboardFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
    users: users.users,
    stats_reports: stats_reports.stats_reports,
  };
  const formLayout: IForm | undefined = useMemo(() => value && WallboardEditForm(value, param_props, t), [value, organizations, users, stats_reports, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && WallboardFormOptions(value, param_props, t), [value, organizations, users, stats_reports, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchWallboard, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchWallboard) => value && save(() => patchWallboard(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onWallboardFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !users.loaded || !stats_reports.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.Wallboard, value)}
    >
      <AtlasForm<ISharedPatchWallboard>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={WallboardComponents}
        additionalParams={WallboardAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={WallboardEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('WALLBOARD')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const WallboardFormEditView = React.memo(withEdit(WallboardFormEdit, getWallboardUpdateSchema));
